import {
  BookOpen,
  Calendar,
  CheckCircle,
  Code,
  Copy,
  Eye,
  EyeOff,
  XCircle
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Button from '../components/Common/Button';
import { useLazyProgrammingCategoryResultQuery } from '../services/startTestAPI/startTestApi';

type CompletedTestResponse = {
  data: CompletedTestData;
  message: string;
  status: string;
};

type CompletedTestData = {
  id: string;
  isActive: boolean;
  userId: string;
  categoryId: string;
  questionId: string;
  testName: string;
  createdDate: string; // ISO date string
  updatedDate: string; // ISO date string
  question: Question;
  answers: Answer;
  testStatus: 'COMPLETED' | string;
};

type Question = {
  id: string;
  isActive: boolean;
  createdDate: string;
  questionTypeId: string;
  roundId: string;
  sectionId: string;
  topicId: string;
  subTopic: string;
  questionText: string;
  explanation: string | null;
  isProgrammingQuestion: boolean;
  options: Option[] | null;
  sourceNameId: string;
  correctAnswer: string | null;
  questionStatus: number;
  categoryId: string[];
};

type Answer = {
  questionId: string;
  userSelectedOption: string;
  isCorrectAnswer: boolean;
  actualCorrectOption: string;
  userSubmittedCode: string;
  languageUsed: string;
  aiGeneratedAnswer: string;
  explanation: string;
};

type Option = {
  id?: string;
  text?: string;
  isCorrect?: boolean;
};

const ProgrammingCategoryResults = () => {
  const [showAIAnswer, setShowAIAnswer] = useState(true);
  const [copiedCode, setCopiedCode] = useState('');
  const [data, setData] = useState<CompletedTestResponse | null>(null);

  const navigate = useNavigate();

  const { categoryId, questionId } = useParams();
  const [getProgrammingCategoryResult] =
    useLazyProgrammingCategoryResultQuery();
  const getResults = async (
    categoryId: string | undefined,
    questionId: string | undefined
  ) => {
    if (categoryId && questionId) {
      try {
        const response = await getProgrammingCategoryResult({
          categoryId,
          questionId
        }).unwrap();
        if (response?.status === 'success') {
          setData(response);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };
  const copyToClipboard = async (code: string, type: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(type);
      setTimeout(() => setCopiedCode(''), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const cleanCode = (code: string) => {
    return code.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n');
  };

  const handleClickExit = () => {
    navigate('/programming-categories');
  };

  useEffect(() => {
    getResults(categoryId, questionId);
  }, [categoryId, questionId]);

  return (
    <div className='min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 sm:p-4'>
      {/* Exit Button */}
      <div className='mx-auto mb-3 flex max-w-6xl justify-end px-2'>
        <Button
          variant='contained'
          label='Exit'
          handleClickBtn={handleClickExit}
          classes='h-[40px] px-4 text-base sm:h-[45px] sm:px-6 sm:text-[21px] font-bold text-white'
        />
      </div>

      <div className='mx-auto max-w-6xl px-2 sm:px-0'>
        {/* Header */}
        <div className='mb-4 rounded-lg bg-white p-4 shadow-lg sm:mb-6 sm:p-6'>
          <div className='mb-4 flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0'>
            {/* Title Section */}
            <div className='flex items-center space-x-3'>
              <div className='rounded-full bg-green-100 p-2'>
                <CheckCircle className='h-5 w-5 text-green-600 sm:h-6 sm:w-6' />
              </div>
              <div>
                <h1 className='text-lg font-bold text-gray-900 sm:text-2xl'>
                  {data?.data?.testName || 'Test Results'}
                </h1>
                <p className='text-sm text-gray-600 sm:text-base'>
                  Programming Question Results
                </p>
              </div>
            </div>

            {/* Status and Action Buttons */}
            <div className='flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-x-3 sm:space-y-0'>
              <div className='rounded-full bg-green-50 px-3 py-1 text-center'>
                <span className='text-sm font-medium text-green-700'>
                  Status: COMPLETED
                </span>
              </div>
            </div>
          </div>

          {/* Test Info */}
          <div className='flex flex-col space-y-2 sm:flex-row sm:space-x-6 sm:space-y-0'>
            <div className='flex items-center space-x-2'>
              <Calendar className='h-4 w-4 text-gray-500' />
              <span className='text-sm text-gray-600'>
                Completed: {formatDate(data?.data?.updatedDate)}
              </span>
            </div>

            <div className='flex items-center space-x-2'>
              {data?.data?.answers?.isCorrectAnswer ? (
                <>
                  <CheckCircle className='h-4 w-4 text-green-500' />
                  <span className='text-sm font-medium text-green-600'>
                    Correct Answer
                  </span>
                </>
              ) : (
                <>
                  <XCircle className='h-4 w-4 text-red-500' />
                  <span className='text-sm font-medium text-red-600'>
                    Incorrect Answer
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        {/* Question Details */}
        <div className='mb-4 rounded-lg bg-white p-4 shadow-lg sm:mb-6 sm:p-6'>
          <div className='mb-4 flex items-center space-x-3'>
            <BookOpen className='h-5 w-5 text-blue-600 sm:h-6 sm:w-6' />
            <h2 className='text-lg font-semibold text-gray-900 sm:text-xl'>
              Question Details
            </h2>
          </div>

          <div className='mb-4 rounded-lg bg-blue-50 p-3 sm:p-4'>
            <p
              className='text-sm text-gray-800 sm:text-base'
              dangerouslySetInnerHTML={{
                __html: data?.data?.question?.questionText ?? '-'
              }}
            />
          </div>

          <div className='grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2'>
            <div className='rounded-lg bg-gray-50 p-3'>
              <span className='text-sm font-medium text-gray-600'>
                Sub Topic:
              </span>
              <p className='text-sm text-gray-800 sm:text-base'>
                {data?.data?.question?.subTopic ?? '-'}
              </p>
            </div>
            <div className='rounded-lg bg-gray-50 p-3'>
              <span className='text-sm font-medium text-gray-600'>
                Language Used:
              </span>
              <p className='text-sm capitalize text-gray-800 sm:text-base'>
                {data?.data?.answers?.languageUsed ?? '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Results - Code Comparison */}
        <div className='grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2'>
          {/* User Submitted Code */}
          <div className='rounded-lg bg-white p-4 shadow-lg sm:p-6'>
            <div className='mb-4 flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0'>
              <div className='flex items-center space-x-2 sm:space-x-3'>
                <Code className='h-5 w-5 text-purple-600 sm:h-6 sm:w-6' />
                <h3 className='text-base font-semibold text-gray-900 sm:text-lg'>
                  Your Submitted Code
                </h3>
              </div>
              <button
                onClick={() =>
                  copyToClipboard(
                    cleanCode(data?.data?.answers?.userSubmittedCode ?? ''),
                    'user'
                  )
                }
                className='flex w-fit items-center space-x-1 rounded-md bg-purple-100 px-3 py-1.5 text-purple-700 transition-colors hover:bg-purple-200'
              >
                <Copy className='h-4 w-4' />
                <span className='text-sm'>
                  {copiedCode === 'user' ? 'Copied!' : 'Copy'}
                </span>
              </button>
            </div>

            <div className='overflow-hidden rounded-lg bg-gray-900'>
              <div className='flex items-center justify-between bg-gray-800 px-3 py-2 sm:px-4'>
                <span className='text-xs font-medium text-gray-300 sm:text-sm'>
                  {data?.data?.answers?.languageUsed}
                </span>
                <div className='flex items-center space-x-1.5 sm:space-x-2'>
                  <div className='h-2.5 w-2.5 rounded-full bg-red-500 sm:h-3 sm:w-3'></div>
                  <div className='h-2.5 w-2.5 rounded-full bg-yellow-500 sm:h-3 sm:w-3'></div>
                  <div className='h-2.5 w-2.5 rounded-full bg-green-500 sm:h-3 sm:w-3'></div>
                </div>
              </div>
              <pre className='max-h-[400px] overflow-auto p-3 text-xs text-gray-100 sm:p-4 sm:text-sm'>
                <code>
                  {cleanCode(data?.data?.answers?.userSubmittedCode ?? 'N/A')}
                </code>
              </pre>
            </div>
          </div>
          {/* AI Generated Answer */}
          <div className='rounded-lg bg-white p-4 shadow-lg sm:p-6'>
            <div className='mb-4 flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0'>
              <div className='flex items-center space-x-2 sm:space-x-3'>
                <div className='rounded-full bg-gradient-to-r from-blue-500 to-purple-600 p-1'>
                  <Code className='h-3 w-3 text-white sm:h-4 sm:w-4' />
                </div>
                <h3 className='text-base font-semibold text-gray-900 sm:text-lg'>
                  AI Reference Solution
                </h3>
              </div>
              <div className='flex items-center space-x-2'>
                <button
                  onClick={() => setShowAIAnswer(!showAIAnswer)}
                  className='flex items-center space-x-1 rounded-md bg-blue-100 px-3 py-1.5 text-blue-700 transition-colors hover:bg-blue-200'
                >
                  {showAIAnswer ? (
                    <EyeOff className='h-4 w-4' />
                  ) : (
                    <Eye className='h-4 w-4' />
                  )}
                  <span className='text-sm'>
                    {showAIAnswer ? 'Hide' : 'Show'}
                  </span>
                </button>
                {showAIAnswer && (
                  <button
                    onClick={() =>
                      copyToClipboard(
                        data?.data?.answers?.aiGeneratedAnswer ?? '',
                        'ai'
                      )
                    }
                    className='flex items-center space-x-1 rounded-md bg-gray-100 px-3 py-1.5 text-gray-700 transition-colors hover:bg-gray-200'
                  >
                    <Copy className='h-4 w-4' />
                    <span className='text-sm'>
                      {copiedCode === 'ai' ? 'Copied!' : 'Copy'}
                    </span>
                  </button>
                )}
              </div>
            </div>

            {showAIAnswer ? (
              <div className='overflow-hidden rounded-lg bg-gray-900'>
                <div className='flex items-center justify-between bg-gray-800 px-3 py-2 sm:px-4'>
                  <span className='text-xs font-medium text-gray-300 sm:text-sm'>
                    {data?.data?.answers?.languageUsed}
                  </span>
                  <div className='flex items-center space-x-1.5 sm:space-x-2'>
                    <div className='h-2.5 w-2.5 rounded-full bg-red-500 sm:h-3 sm:w-3'></div>
                    <div className='h-2.5 w-2.5 rounded-full bg-yellow-500 sm:h-3 sm:w-3'></div>
                    <div className='h-2.5 w-2.5 rounded-full bg-green-500 sm:h-3 sm:w-3'></div>
                  </div>
                </div>
                <pre className='max-h-[400px] overflow-auto p-3 text-xs text-gray-100 sm:p-4 sm:text-sm'>
                  <code>{data?.data?.answers?.aiGeneratedAnswer ?? 'N/A'}</code>
                </pre>
              </div>
            ) : (
              <div className='rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 p-6 text-center sm:p-8'>
                <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-600 p-2 sm:mb-4 sm:h-16 sm:w-16 sm:p-3'>
                  <Eye className='h-6 w-6 text-white sm:h-8 sm:w-8' />
                </div>
                <p className='mb-3 text-sm text-gray-600 sm:mb-4 sm:text-base'>
                  Click &quot;Show&quot; to view the AI-generated reference
                  solution
                </p>
                <p className='text-xs text-gray-500 sm:text-sm'>
                  Compare your approach with our optimized solution
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Explanation Section */}
        <div className='mt-4 w-full px-2 py-4 sm:mt-6 sm:px-4 sm:py-6'>
          <div className='w-full'>
            <div className='mb-3 sm:mb-4'>
              <h3 className='text-lg font-semibold text-gray-900 sm:text-xl'>
                Explanation
              </h3>
            </div>

            <div className='w-full rounded-r-lg border-l-4 border-blue-500 bg-blue-50 p-4 shadow-sm sm:p-6'>
              <p className='text-sm leading-relaxed text-gray-800 sm:text-base'>
                {data?.data?.answers?.explanation?.trim() ||
                  'No explanation available.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgrammingCategoryResults;
